import { Fragment, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Collapse, Container, Group, Loader,
  Paper, ScrollArea, Select, Stack, Table, Text, TextInput, Title,
} from '@mantine/core';

const PERIODS = [
  { value: 'today', label: 'Today (ET)' },
  { value: '7d', label: 'Last 7 calendar days (ET)' },
  { value: '30d', label: 'Last 30 calendar days (ET)' },
  { value: 'month', label: 'This month (ET)' },
  { value: 'all', label: 'All recorded history' },
];

function usd(value) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n !== 0 && Math.abs(n) < 0.0001) return `$${n.toFixed(8)}`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(n);
}

function dateET(value) {
  if (!value) return '—';
  const t = new Date(value);
  if (Number.isNaN(t.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(t);
}

function nameFor(row) {
  const name = [row.firstname, row.lastname].filter(Boolean).join(' ');
  if (name && row.emailaddress) return `${name} (${row.emailaddress})`;
  return name || row.emailaddress || row.userid || 'Anonymous';
}

async function getJson(path, signal) {
  const r = await fetch(path, { credentials: 'same-origin', cache: 'no-store', signal });
  if (r.status === 401 || r.status === 403) {
    throw new Error('Administrator sign-in is required to view this report.');
  }
  if (!r.ok) throw new Error(`Report request failed (HTTP ${r.status}).`);
  return r.json();
}

function Breakdown({ title, rows, primaryKey, primaryLabel, includeQueries = false }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Title order={3} size="h4" mb="sm">{title}</Title>
      <ScrollArea>
        <Table striped highlightOnHover withTableBorder miw={390}>
          <Table.Thead><Table.Tr>
            <Table.Th>{primaryLabel}</Table.Th>
            {includeQueries && <Table.Th ta="right">Queries</Table.Th>}
            <Table.Th ta="right">API calls</Table.Th>
            <Table.Th ta="right">Known cost</Table.Th>
            <Table.Th ta="right">Unpriced</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {(rows || []).length === 0 ? (
              <Table.Tr><Table.Td colSpan={includeQueries ? 5 : 4}><Text c="dimmed">No recorded activity.</Text></Table.Td></Table.Tr>
            ) : (rows || []).map((r, index) => (
              <Table.Tr key={`${r[primaryKey] ?? 'none'}-${index}`}>
                <Table.Td>{primaryKey === 'userid' ? nameFor(r) : (r[primaryKey] || 'Unknown')}</Table.Td>
                {includeQueries && <Table.Td ta="right">{r.queries}</Table.Td>}
                <Table.Td ta="right">{r.operations}</Table.Td>
                <Table.Td ta="right">{usd(r.known_cost_usd)}</Table.Td>
                <Table.Td ta="right">{r.unpriced_operations > 0 ? <Badge color="orange" variant="light">{r.unpriced_operations}</Badge> : '0'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

function OperationDetail({ queryId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError('');
    getJson(`/admin/api/accounting/queries/${encodeURIComponent(queryId)}/operations`, controller.signal)
      .then(setData).catch((err) => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, [queryId]);
  if (error) return <Alert color="red">{error}</Alert>;
  if (!data) return <Loader size="sm" />;
  return (
    <Stack gap="xs" py="sm">
      <Text size="xs" c="dimmed">Execution ID: {queryId}</Text>
      {data.truncated && <Alert color="yellow">Showing the first 500 API operations for this query.</Alert>}
      <ScrollArea>
        <Table striped miw={860} withTableBorder>
          <Table.Thead><Table.Tr>
            <Table.Th>Operation</Table.Th><Table.Th>Model</Table.Th><Table.Th>Status</Table.Th>
            <Table.Th ta="right">Input</Table.Th><Table.Th ta="right">Cached</Table.Th>
            <Table.Th ta="right">Output</Table.Th><Table.Th ta="right">Cost</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {data.items.map((op) => <Table.Tr key={op.operation_id}>
              <Table.Td>{op.source_component}: {op.operation_type}</Table.Td>
              <Table.Td>{op.model}</Table.Td><Table.Td>{op.status}</Table.Td>
              <Table.Td ta="right">{op.input_tokens ?? '—'}</Table.Td>
              <Table.Td ta="right">{op.cached_input_tokens ?? '—'}</Table.Td>
              <Table.Td ta="right">{op.output_tokens ?? '—'}</Table.Td>
              <Table.Td ta="right">{op.cost_status === 'estimated' ? usd(op.estimated_cost_usd) : 'Unknown'}</Table.Td>
            </Table.Tr>)}
            {data.items.length === 0 && <Table.Tr><Table.Td colSpan={7}>No API operations recorded.</Table.Td></Table.Tr>}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
}

function QueryRows({ period }) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  useEffect(() => { setPage(1); setExpanded(null); }, [period, search]);
  useEffect(() => {
    const controller = new AbortController();
    setError('');
    setData(null);
    const params = new URLSearchParams({ period, page: String(page), page_size: '25' });
    if (search) params.set('search', search);
    getJson(`/admin/api/accounting/queries?${params.toString()}`, controller.signal)
      .then(setData).catch((err) => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, [page, period, search]);
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="md">
        <Title order={3} size="h4">Individual query executions</Title>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); setExpanded(null); }}>
          <Group gap="xs" align="flex-end">
            <TextInput label="Find a question, user ID, or endpoint" value={searchInput} onChange={(e) => setSearchInput(e.currentTarget.value)} maxLength={120} style={{ flex: 1, minWidth: 180 }} />
            <Button type="submit">Search</Button>
            <Button variant="subtle" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>Clear</Button>
          </Group>
        </form>
        {error && <Alert color="red">{error}</Alert>}
        {!data && !error && <Loader size="sm" />}
        {data && <>
          <Text size="xs" c="dimmed">{data.total} executions · Page {page} · Select a row to view its individual API calls.</Text>
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder miw={820}>
              <Table.Thead><Table.Tr>
                <Table.Th>When (ET)</Table.Th><Table.Th>User</Table.Th>
                <Table.Th>Endpoint / question</Table.Th><Table.Th ta="right">Calls</Table.Th>
                <Table.Th ta="right">Known cost</Table.Th><Table.Th ta="right">Unpriced</Table.Th>
                <Table.Th>Details</Table.Th>
              </Table.Tr></Table.Thead>
              <Table.Tbody>
                {data.items.map((q) => <Fragment key={q.query_id}>
                  <Table.Tr key={q.query_id}>
                    <Table.Td>{dateET(q.started_at)}</Table.Td>
                    <Table.Td>{nameFor(q)}</Table.Td>
                    <Table.Td style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                      <Text size="xs" c="dimmed">{q.endpoint} · {q.status}</Text>
                      <Text size="sm">{q.query_text}</Text>
                    </Table.Td>
                    <Table.Td ta="right">{q.operations}</Table.Td>
                    <Table.Td ta="right">{usd(q.known_cost_usd)}</Table.Td>
                    <Table.Td ta="right">{q.unpriced_operations || 0}</Table.Td>
                    <Table.Td>
                      <Button size="compact-sm" variant="light" aria-expanded={expanded === q.query_id} onClick={() => setExpanded(expanded === q.query_id ? null : q.query_id)}>
                        {expanded === q.query_id ? 'Hide' : 'View'}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                  {expanded === q.query_id && <Table.Tr key={`${q.query_id}-details`}><Table.Td colSpan={7}>
                    <OperationDetail queryId={q.query_id} />
                  </Table.Td></Table.Tr>}
                </Fragment>)}
                {data.items.length === 0 && <Table.Tr><Table.Td colSpan={7}>No matching query executions.</Table.Td></Table.Tr>}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <Group justify="space-between">
            <Button variant="light" disabled={page <= 1} onClick={() => { setPage((n) => n - 1); setExpanded(null); }}>Previous</Button>
            <Text size="sm" c="dimmed">{page} / {Math.max(1, Math.ceil(data.total / 25))}</Text>
            <Button variant="light" disabled={page * 25 >= data.total} onClick={() => { setPage((n) => n + 1); setExpanded(null); }}>Next</Button>
          </Group>
        </>}
      </Stack>
    </Paper>
  );
}

export default function AccountingReport() {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError('');
    getJson(`/admin/api/accounting/overview?period=${encodeURIComponent(period)}`, controller.signal)
      .then(setData).catch((err) => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, [period, refresh]);
  const s = data?.summary;
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div><Title order={2}>AI Accounting</Title><Text c="dimmed">Admin Tools / Reports / Accounting</Text></div>
          <Button component="a" href="/" variant="light">Back to Search</Button>
        </Group>
        <Group align="flex-end">
          <Select label="Report period" value={period} onChange={(v) => { if (v) setPeriod(v); }} data={PERIODS} w={260} allowDeselect={false} />
          <Button variant="light" onClick={() => setRefresh((x) => x + 1)}>Refresh</Button>
        </Group>
        <Text size="sm" c="dimmed">All calendar-day boundaries use America/New_York. Figures are estimated USD charges from recorded API usage. Each backend HTTP request is a separate query execution, even when one click makes several requests.</Text>
        {error && <Alert color="red">{error}</Alert>}
        {!data && !error && <Loader />}
        {s && <>
          <Group grow align="stretch">
            <Card withBorder padding="md"><Text size="sm" c="dimmed">Known estimated cost</Text><Title order={2}>{usd(s.known_cost_usd)}</Title></Card>
            <Card withBorder padding="md"><Text size="sm" c="dimmed">Query executions</Text><Title order={2}>{s.queries}</Title></Card>
            <Card withBorder padding="md"><Text size="sm" c="dimmed">AI API operations</Text><Title order={2}>{s.operations}</Title></Card>
            <Card withBorder padding="md"><Text size="sm" c="dimmed">Unpriced API calls</Text><Title order={2}>{s.unpriced_operations}</Title></Card>
          </Group>
          {Number(s.unpriced_operations) > 0 && <Alert color="yellow">Costs are incomplete: {s.unpriced_operations} API operations have unavailable pricing or usage. Known cost is not the full total.</Alert>}
          {Number(s.unfinished_queries) > 0 && <Alert color="yellow">{s.unfinished_queries} query executions are still marked started; their records may be incomplete.</Alert>}
          <Group grow align="flex-start">
            <Breakdown title="By user" rows={data.by_user} primaryKey="userid" primaryLabel="User" includeQueries />
            <Breakdown title="By model" rows={data.by_model} primaryKey="model" primaryLabel="Model" />
          </Group>
          <Group grow align="flex-start">
            <Breakdown title="By operation" rows={data.by_operation} primaryKey="operation_type" primaryLabel="Operation" />
            <Breakdown title="By endpoint" rows={data.by_endpoint} primaryKey="endpoint" primaryLabel="Endpoint" includeQueries />
          </Group>
        </>}
        <QueryRows period={period} />
        <Text c="dimmed" size="xs">The report reads stored PostgreSQL records and does not make any AI API requests. Known charges may differ from final provider invoices.</Text>
      </Stack>
    </Container>
  );
}
